import { createSignal, createResource, type Accessor } from "solid-js";
import { getOrgSettings, putOrgSettings } from "./ganforge";

// Local cache for optimistic updates
const [vcsEnabledOverrides, setVcsEnabledOverrides] = createSignal<Record<string, boolean>>({});

export function useVcsEnabled(orgAccessor: () => string): Accessor<boolean> {
  const [data] = createResource(
    orgAccessor,
    async (org) => {
      if (!org) return true;
      try {
        const readout = await getOrgSettings(org);
        const val = readout?.keys?.vcs_code_management_enabled?.value;
        if (typeof val === "boolean") return val;
        return true;
      } catch {
        return true;
      }
    },
    { initialValue: true }
  );

  return () => {
    const org = orgAccessor();
    const overrides = vcsEnabledOverrides();
    if (org && typeof overrides[org] === "boolean") {
      return overrides[org];
    }
    return data() ?? true;
  };
}

export async function setVcsEnabled(org: string, enabled: boolean): Promise<boolean> {
  // Optimistic update
  setVcsEnabledOverrides((prev) => ({ ...prev, [org]: enabled }));
  try {
    await putOrgSettings(org, { vcs_code_management_enabled: enabled });
    return enabled;
  } catch (err) {
    // Revert on error
    console.error("Failed to update vcs_code_management_enabled setting:", err);
    setVcsEnabledOverrides((prev) => {
      const next = { ...prev };
      delete next[org];
      return next;
    });
    throw err;
  }
}
